/**
 * The Alinq Address + the connect flow — the heart of the product.
 *
 * - A handle (@username#tag) is the human-facing identifier; it resolves to a
 *   self-signed AgentCard and therefore to a DID.
 * - Sharing an address is only an invitation. A link must be REQUESTED and
 *   ACCEPTED (with a pairing code and a trust tier) before two agents connect.
 * - The resulting relationship is CO-SIGNED by both DIDs, so neither side can
 *   unilaterally claim or upgrade the tier.
 */
import { base32, base64urlnopad as b64u } from "@scure/base";
import type {
  AgentCard,
  DID,
  LinkPayload,
  PairingCode,
  SignedAgentCard,
  SignedRelationship,
  TrustTier,
} from "@alinq/proto";
import {
  b64uDecode,
  b64uEncode,
  canonicalJson,
  hmacSha256,
  sha256Bytes,
  sign,
  verifyByDid,
  type Identity,
} from "./crypto.js";

const utf8 = (s: string): Uint8Array => new TextEncoder().encode(s);
const fromUtf8 = (b: Uint8Array): string => new TextDecoder().decode(b);

const TAG_LEN = 4; // hex characters
const USERNAME_RE = /^[a-z0-9._]{3,30}$/;

// --- handle (@username#tag) -------------------------------------------------

function bytesToHex(b: Uint8Array): string {
  return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}

/** Build the canonical handle for a username + DID. The tag is deterministic from the DID. */
export function makeHandle(username: string, did: DID): string {
  if (!USERNAME_RE.test(username)) {
    throw new Error("invalid username: use a-z 0-9 . _ , 3-30 chars");
  }
  const tag = bytesToHex(sha256Bytes(utf8(did))).slice(0, TAG_LEN).toUpperCase();
  return `@${username}#${tag}`;
}

/** Recompute the expected tag for a DID and check it matches the handle's tag. */
export function handleMatchesDid(handle: string, did: DID): boolean {
  const m = /^@([a-z0-9._]{3,30})#([0-9A-F]{4})$/.exec(handle);
  if (!m) return false;
  return makeHandle(m[1]!, did) === handle;
}

// --- signed agent card ------------------------------------------------------

/**
 * Self-sign an agent card (handle included) so anyone can verify handle<->DID via
 * the DID. Any pre-existing `signature` is dropped before signing, so re-signing
 * an already-signed card (e.g. a profile update) is idempotent and verifiable.
 */
export function signCard(
  card: AgentCard & { handle: string; signature?: string },
  signPriv: Uint8Array,
): SignedAgentCard {
  const { signature: _drop, ...core } = card;
  const bytes = utf8(canonicalJson(core));
  return { ...core, signature: b64uEncode(sign(bytes, signPriv)) };
}

/** Verify a card's self-signature AND that its handle's tag really derives from its DID. */
export function verifyCard(card: SignedAgentCard): boolean {
  const { signature, ...rest } = card;
  if (!signature) return false;
  if (!handleMatchesDid(card.handle, card.did)) return false;
  return verifyByDid(b64uDecode(signature), utf8(canonicalJson(rest)), card.did);
}

// --- invite URL (card / QR / NFC / link-in-email all reuse this) ------------

/** alinq://invite?card=<b64url(card)>&code=<pairingCode>&tier=<n> */
export function encodeInvite(card: SignedAgentCard, code: string, tier: TrustTier): string {
  const cardParam = b64u.encode(utf8(JSON.stringify(card)));
  return `alinq://invite?card=${cardParam}&code=${encodeURIComponent(code)}&tier=${tier}`;
}

export interface DecodedInvite {
  card: SignedAgentCard;
  code: string;
  tier: TrustTier;
}

export function decodeInvite(url: string): DecodedInvite {
  if (!url.startsWith("alinq://invite?")) throw new Error("not an alinq invite URL");
  const params = new URLSearchParams(url.slice("alinq://invite?".length));
  const cardParam = params.get("card");
  const code = params.get("code");
  const tierRaw = params.get("tier");
  if (!cardParam || !code || tierRaw === null) throw new Error("invite missing card/code/tier");
  const card = JSON.parse(fromUtf8(b64u.decode(cardParam))) as SignedAgentCard;
  const tier = Number(tierRaw);
  if (![0, 1, 2, 3].includes(tier)) throw new Error("invite has an invalid tier");
  return { card, code, tier: tier as TrustTier };
}

// --- pairing codes ----------------------------------------------------------

/**
 * Issue a single-use, time-limited pairing code granting a starting tier.
 * `now` is injected (epoch ms) so callers control time (and tests are deterministic).
 */
export function makePairingCode(
  tier: TrustTier,
  ttlMs: number,
  now: number,
  randomBytes: (n: number) => Uint8Array,
): PairingCode {
  const code = base32.encode(randomBytes(5)).slice(0, 8); // 8 base32 chars
  return { code, tier, expiresAt: now + ttlMs };
}

/**
 * Pairing-code proof: HMAC(code, sessionNonce). The raw code never travels on
 * the wire and the proof is bound to a session nonce, so it cannot be replayed.
 */
export function proveCode(code: string, sessionNonce: string): string {
  return hmacSha256(code, sessionNonce);
}

/** Verify a code proof against an issued pairing code, checking expiry too. */
export function verifyCodeProof(
  issued: PairingCode,
  sessionNonce: string,
  proof: string,
  now: number,
): boolean {
  if (now > issued.expiresAt) return false;
  return timingSafeEqualStr(proveCode(issued.code, sessionNonce), proof);
}

function timingSafeEqualStr(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

// --- connect flow (co-signed relationship) ----------------------------------

/** The bytes both parties sign for a relationship: the core, with stable key order. */
function relationshipCore(a: DID, b: DID, tier: TrustTier, establishedAt: number): Uint8Array {
  return utf8(canonicalJson({ a, b, tier, establishedAt }));
}

/** Step 2 (requester B): build a link.request carrying B's signed card + a code proof. */
export function buildLinkRequest(
  myCard: SignedAgentCard,
  requestedTier: TrustTier,
  code: string | undefined,
  sessionNonce: string,
): Extract<LinkPayload, { type: "link.request" }> {
  const req: Extract<LinkPayload, { type: "link.request" }> = {
    type: "link.request",
    from: myCard,
    requestedTier,
  };
  if (code !== undefined) req.codeProof = proveCode(code, sessionNonce);
  return req;
}

export interface AcceptResult {
  grantedTier: TrustTier;
  /** Co-signing in progress: sigA (acceptor) set, sigB empty until the requester counter-signs. */
  relationship: SignedRelationship;
  payload: Extract<LinkPayload, { type: "link.accept" }>;
}

/**
 * Step 3 (acceptor A): verify the request and produce a half-signed relationship.
 * - Verifies the requester's card self-signature.
 * - Verifies the pairing-code proof (if a code was required) and its expiry.
 * - Clamps the granted tier: grantedTier <= min(requestedTier, code tier, optional explicit cap).
 * Throws if the request is not acceptable (bad card, bad/expired code, etc).
 */
export function acceptLink(args: {
  request: Extract<LinkPayload, { type: "link.request" }>;
  me: Identity;
  issuedCode?: PairingCode;
  sessionNonce: string;
  now: number;
  /** Optional explicit cap chosen by the acceptor (may grant lower than the code allows). */
  grantTier?: TrustTier;
}): AcceptResult {
  const { request, me, issuedCode, sessionNonce, now, grantTier } = args;

  if (!verifyCard(request.from)) throw new Error("requester card failed verification");

  // A code is required iff the acceptor issued one. No code path that skips the gate.
  if (issuedCode) {
    if (!request.codeProof) throw new Error("pairing code required but no proof supplied");
    if (!verifyCodeProof(issuedCode, sessionNonce, request.codeProof, now)) {
      throw new Error("pairing-code proof invalid or expired");
    }
  }

  const codeCap: TrustTier = issuedCode ? issuedCode.tier : 0;
  let granted: TrustTier = Math.min(request.requestedTier, codeCap) as TrustTier;
  if (grantTier !== undefined) granted = Math.min(granted, grantTier) as TrustTier;

  const establishedAt = now;
  const a = me.did;
  const b = request.from.did;
  const core = relationshipCore(a, b, granted, establishedAt);
  const relationship: SignedRelationship = {
    a,
    b,
    tier: granted,
    establishedAt,
    sigA: b64uEncode(sign(core, me.signPriv)),
    sigB: "",
  };
  return {
    grantedTier: granted,
    relationship,
    payload: { type: "link.accept", grantedTier: granted, relationship },
  };
}

/**
 * Step 4 (requester B): verify the acceptor's half-signature, then counter-sign.
 * Returns the fully co-signed relationship; both sides store this.
 */
export function counterSign(relationship: SignedRelationship, me: Identity): SignedRelationship {
  if (me.did !== relationship.b) throw new Error("counter-signer is not party b of this relationship");
  const core = relationshipCore(relationship.a, relationship.b, relationship.tier, relationship.establishedAt);
  if (!verifyByDid(b64uDecode(relationship.sigA), core, relationship.a)) {
    throw new Error("acceptor signature (sigA) invalid");
  }
  return { ...relationship, sigB: b64uEncode(sign(core, me.signPriv)) };
}

/** Verify a fully co-signed relationship: both sigA (by a) and sigB (by b) over the core. */
export function verifyRelationship(rel: SignedRelationship): boolean {
  if (!rel.sigA || !rel.sigB) return false;
  const core = relationshipCore(rel.a, rel.b, rel.tier, rel.establishedAt);
  return (
    verifyByDid(b64uDecode(rel.sigA), core, rel.a) && verifyByDid(b64uDecode(rel.sigB), core, rel.b)
  );
}

/**
 * Downgrade (or revoke to tier 0) — re-issues a co-signed relationship at a LOWER
 * tier, effective immediately. Refuses to raise the tier (no unilateral upgrade).
 */
export function downgradeRelationship(
  rel: SignedRelationship,
  newTier: TrustTier,
  initiator: Identity,
  now: number,
): Extract<LinkPayload, { type: "link.downgrade" }> {
  if (newTier >= rel.tier) throw new Error("downgrade must lower the tier");
  if (initiator.did !== rel.a && initiator.did !== rel.b) {
    throw new Error("only a party of the relationship may downgrade it");
  }
  const core = relationshipCore(rel.a, rel.b, newTier, now);
  // The initiator signs their half immediately; the counterparty re-signs on receipt.
  const isA = initiator.did === rel.a;
  const next: SignedRelationship = {
    a: rel.a,
    b: rel.b,
    tier: newTier,
    establishedAt: now,
    sigA: isA ? b64uEncode(sign(core, initiator.signPriv)) : "",
    sigB: isA ? "" : b64uEncode(sign(core, initiator.signPriv)),
  };
  return { type: "link.downgrade", tier: newTier, relationship: next };
}
